// CompletedMatches.jsx
import React, { useState, useEffect } from 'react';
import './CompletedMatches.css';

const CompletedMatches = () => {
  const [completedMatches, setCompletedMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Helper function to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  useEffect(() => {
    fetchCompletedMatches();
  }, []);

  const fetchCompletedMatches = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://api.asterickx.com/api/Matches', {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        if (response.status === 401) {
          setError('Authentication failed. Please login again.');
        } else {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        setLoading(false);
        return;
      }

      const data = await response.json();
      console.log('=== MATCHES API RESPONSE ===');
      console.log('Full data:', data);

      // Handle both array and $values wrapper format
      let matchesArray = [];
      if (Array.isArray(data)) {
        matchesArray = data;
      } else if (data.$values && Array.isArray(data.$values)) {
        matchesArray = data.$values;
      }

      // Filter only completed matches
      const completed = matchesArray.filter(match => {
        const status = match.status || match.Status;
        return status && status.toLowerCase() === 'completed';
      });

      console.log('Completed matches count:', completed.length);

      // Map the matches to the required format
      const formattedMatches = completed.map(match => {
        const isTeamMatch = match.isTeamMatch || match.IsTeamMatch || false;
        
        return {
          id: match.id || match.Id,
          matchNumber: match.matchNumber || match.MatchNumber,
          type: isTeamMatch ? 'team' : 'single',
          
          // Tournament and Event Info
          tournamentName: match.tournamentName || match.TournamentName || 'N/A',
          eventName: match.eventName || match.EventName || match.eventCategoryDisplayName || match.EventCategoryDisplayName || 'N/A',
          roundName: match.roundName || match.RoundName || 'N/A',
          
          // Team/Player Names
          team1: match.team1Name || match.Team1Name || match.player1AName || match.Player1AName || 'N/A',
          team2: match.team2Name || match.Team2Name || match.player2AName || match.Player2AName || 'N/A',
          player1: match.team1Name || match.Team1Name || match.player1AName || match.Player1AName || 'N/A',
          player2: match.team2Name || match.Team2Name || match.player2AName || match.Player2AName || 'N/A',
          
          // Match Details
          matchDateTime: match.matchDateTime || match.MatchDateTime,
          courtNumber: match.courtNumber || match.CourtNumber || 'N/A',
          status: match.status || match.Status,
          
          // Score and Winner (you may need to add these fields to your API or calculate them)
          score: match.score || match.Score || 'TBD',
          winner: match.winner || match.Winner || 'TBD',
          
          // Additional data for team matches
          team1Players: isTeamMatch ? [
            match.player1AName || match.Player1AName,
            match.player1BName || match.Player1BName
          ].filter(Boolean) : null,
          team2Players: isTeamMatch ? [
            match.player2AName || match.Player2AName,
            match.player2BName || match.Player2BName
          ].filter(Boolean) : null,
          
          // Original match data for reference
          originalData: match
        };
      });

      console.log('Formatted completed matches:', formattedMatches);
      setCompletedMatches(formattedMatches);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching completed matches:', error);
      setError('Failed to load completed matches. Please try again.');
      setLoading(false);
    }
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return 'N/A';
    try {
      const date = new Date(dateTimeString);
      const dateStr = date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
      const timeStr = date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
      return `${dateStr} | ${timeStr}`;
    } catch {
      return 'N/A';
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="completed-matches-container">
        <h1>Completed Matches</h1>
        <div className="no-matches">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="completed-matches-container">
      <h1>Completed Matches</h1>

      {completedMatches.length === 0 ? (
        <div className="no-matches">No completed matches found.</div>
      ) : (
        <div className="matches-list">
          {completedMatches.map((match) => (
            <div key={match.id} className="match-card">
              <div className="match-header">
                <span className="round">{match.roundName}</span>
                <span className="match-type">{match.type === 'team' ? 'Team' : 'Singles'}</span>
                <span className="date-time">{formatDateTime(match.matchDateTime)}</span>
              </div>

              <div className="match-body">
                {/* Competitor 1 */}
                <div className={`competitor ${
                  (match.type === 'team' ? match.winner === match.team1 : match.winner === match.player1) 
                    ? 'winner' 
                    : ''
                }`}>
                  {match.type === 'team' ? (
                    <>
                      <div className="team-name">{match.team1}</div>
                      {match.team1Players && match.team1Players.length > 0 && (
                        <div className="players">
                          {match.team1Players.join(', ')}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="player-name">{match.player1}</div>
                  )}
                </div>

                {/* Score */}
                <div className="score">{match.score}</div>

                {/* Competitor 2 */}
                <div className={`competitor ${
                  (match.type === 'team' ? match.winner === match.team2 : match.winner === match.player2) 
                    ? 'winner' 
                    : ''
                }`}>
                  {match.type === 'team' ? (
                    <>
                      <div className="team-name">{match.team2}</div>
                      {match.team2Players && match.team2Players.length > 0 && (
                        <div className="players">
                          {match.team2Players.join(', ')}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="player-name">{match.player2}</div>
                  )}
                </div>
              </div>

              <div className="match-footer">
                Winner: <strong>{match.winner}</strong>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CompletedMatches;